/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { OmniconvertConfig } from './types';

/** Explore dashboard / overlay host from the legacy SFRA dns-prefetch. */
export const OMNICONVERT_APP_ORIGIN = 'https://app.omniconvert.com';

/** CDN that serves the Explore tracker (`q553a22.js` in the SFRA htmlHead snippet). */
export const OMNICONVERT_CDN_ORIGIN = 'https://cdn-b.omniconvert.com';

/**
 * Alternate tracker CDN referenced in Omniconvert's current install docs.
 * Overlays and newer snippets may load assets from this host.
 */
export const OMNICONVERT_CDN_ALT_ORIGIN = 'https://cdn.omniconvert.com';

const TRACKER_ID_PATTERN = /^[a-zA-Z0-9]+$/;

export function isValidOmniconvertTrackerId(value: string | undefined): value is string {
    return typeof value === 'string' && TRACKER_ID_PATTERN.test(value);
}

export function isOmniconvertEnabled(config: OmniconvertConfig | undefined): config is OmniconvertConfig {
    return Boolean(config?.enabled) && isValidOmniconvertTrackerId(config?.trackerId);
}

export function getOmniconvertScriptUrl(config: OmniconvertConfig | undefined): string | undefined {
    if (!isOmniconvertEnabled(config)) {
        return undefined;
    }
    return `${OMNICONVERT_CDN_ORIGIN}/js/${config.trackerId}.js`;
}

export function getOmniconvertCspOrigins(): string[] {
    return [OMNICONVERT_CDN_ORIGIN, OMNICONVERT_CDN_ALT_ORIGIN, OMNICONVERT_APP_ORIGIN];
}
